import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, X, Inbox } from "lucide-react";
import { api, errText, photoUrl } from "../../lib/api";
import { compressImage } from "../../lib/image";
import { useLanguage } from "../../context/LanguageContext";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { UpdatePost } from "../LitterUpdatePost";
import { UpdateReactions } from "../LitterUpdateReactions";

const MAX = 6;

export function LitterUpdatesManager({ litter, onClose }) {
  const { t, lang } = useLanguage();
  const [text, setText] = useState("");
  const [tell, setTell] = useState(true);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [items, setItems] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const load = () => litter && api.get(`/litters/${litter.id}/updates`).then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { setText(""); setPhotos([]); load(); }, [litter]); // eslint-disable-line react-hooks/exhaustive-deps

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (photos.length + files.length > MAX) toast.error(t("lu_max"));
    setUploading(true);
    try {
      for (const file of files.slice(0, MAX - photos.length)) {
        const fd = new FormData();
        fd.append("file", await compressImage(file));
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.path]);
      }
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };
  const post = async () => {
    if (!photos.length && !text.trim()) return toast.error(t("lu_need"));
    setBusy(true);
    try {
      const { data } = await api.post(`/my/litters/${litter.id}/updates`, { text, photos, notify_waitlist: tell });
      toast.success(t("lu_posted"), data.sent_to_waitlist ? { description: lang === "fr" ? `Envoyée dans la boîte de réception de ${data.sent_to_waitlist} personne(s) en attente.` : `Sent to the inbox of ${data.sent_to_waitlist} ${data.sent_to_waitlist === 1 ? "person" : "people"} on your waitlist.` } : undefined);
      if (data.notify_error) toast.warning(data.notify_error);
      setText(""); setPhotos([]); load();
    } catch (e) { toast.error(errText(e)); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    try { await api.delete(`/my/litters/${litter.id}/updates/${id}`); load(); } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Dialog open={!!litter} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto bg-white" data-testid="litter-updates-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-[#12234A]">{t("lu_title")}</DialogTitle>
          <DialogDescription className="text-slate-500">{t("lu_desc")}</DialogDescription>
        </DialogHeader>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={1000} rows={3} placeholder={t("lu_text_ph")} data-testid="litter-update-text" className="rounded-xl" />
        <div className="flex flex-wrap gap-2">
          {photos.map((p) => (
            <div key={p} className="relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200">
              <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
              <button type="button" onClick={() => setPhotos((l) => l.filter((x) => x !== p))} aria-label="Remove photo" data-testid="litter-update-photo-remove" className="absolute top-1 right-1 bg-black/55 text-white rounded-full p-0.5"><X size={11} /></button>
            </div>
          ))}
          {photos.length < MAX && (
            <label className="h-20 w-20 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#F5551E] flex flex-col items-center justify-center text-[10px] text-slate-400 cursor-pointer text-center" data-testid="litter-update-photo-upload">
              {uploading ? <Loader2 size={16} className="animate-spin text-[#F5551E]" /> : <ImagePlus size={16} />} {t("lu_add_photos")}
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
            </label>
          )}
        </div>
        {litter?.waitlist_count > 0 && (
          <label className="flex items-center gap-2 text-sm text-[#12234A] bg-[#FFF4F0] rounded-xl px-3 py-2.5 cursor-pointer" data-testid="litter-update-tell-box">
            <Checkbox checked={tell} onCheckedChange={(v) => setTell(!!v)} data-testid="litter-update-tell" />
            <Inbox size={15} className="text-[#F5551E]" />
            {lang === "fr" ? `Envoyer dans la boîte de réception de ma liste d'attente (${litter.waitlist_count})` : `Send to my waitlist's inbox (${litter.waitlist_count})`}
          </label>
        )}
        <Button onClick={post} disabled={busy || uploading} data-testid="litter-update-post" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white gap-1.5">{busy && <Loader2 size={15} className="animate-spin" />} {t("lu_post")}</Button>
        <div className="space-y-2">
          {items.length === 0 ? <p className="text-xs text-slate-400">{t("lu_none")}</p> : items.map((u) => (
            <UpdatePost key={u.id} u={u} lang={lang} footer={<UpdateReactions update={u} />} action={
              confirmId === u.id
                ? <button onClick={() => { setConfirmId(null); del(u.id); }} data-testid={`litter-update-delete-confirm-${u.id}`} className="text-[11px] font-bold text-red-600 hover:underline">{t("lu_confirm")}</button>
                : <button onClick={() => setConfirmId(u.id)} data-testid={`litter-update-delete-${u.id}`} aria-label={t("lu_delete")} className="text-slate-400 hover:text-red-500"><Trash2 size={14} /></button>} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
